import React, { useEffect, useReducer, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Card } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import LoadingOverlay from '../../common/LoadingOverlay';
import ScanPointsListItem from './ScanPointsListItem/ScanPointsListItem';
import Pagination from '../../common/Pagination';
import { useTranslation } from 'react-i18next';
import api from './../../../api';
import handleClientSideSorting from '../../../utils/handleClientSideSorting';
import { toastrAdd } from '../../../actions';

const initialState = {
    scanPoints: [],
    scanPointsTotal: 0,
    loading: true,
    pageCount: 0,
    params: {
        page: 1,
        limit: 25,
        sorter: { column: 'timeCreated', direction: 'desc' },
        clientSideSort: false,
        search: null,
        searchColumns: [
            { column: 'name' },
            { column: 'description' },
            { column: 'location' }
        ]
    }
};

const reducer = (state, action) => {
    switch(action.type) {
        case 'LOADING':
            return {
                ...state,
                loading: action.loading
            };
        case 'SET_SCAN_POINTS':
            return {
                ...state,
                scanPoints: action.scanPoints,
                scanPointsTotal: action.scanPointsTotal,
                pageCount: action.pageCount,
                loading: false
            };
        case 'SET_PARAMS':
            return {
                ...state,
                params: action.params
            };
        default:
            return state;
    };
};

function ScanPointsList(props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { eventID, limit, overView, isScanPointSalesEnabled } = props;
    const partnerID = localStorage.getItem('partnerID');
    const [state, stateDispatch] = useReducer(reducer, {
        ...initialState,
        params: { ...initialState.params, limit }
    });
    const { scanPoints, scanPointsTotal, params, pageCount, loading } = state;
    const sortingOptions = [
        { value: '{ "column": "timeModified", "direction": "desc" }', text: `${t('general.modified')} ↓`},
        { value: '{ "column": "timeModified", "direction": "asc" }', text: `${t('general.modified')} ↑`},
        { value: '{ "column": "name", "direction": "asc" }', text: `${t('general.name')} (A-Z)`},
        { value: '{ "column": "name", "direction": "desc" }', text: `${t('general.name')} (Z-A)`},
        { value: '{ "column": "scanCount", "direction": "desc", "clientSideSort": true, "type": "numeric" }', text: `${t('scan_points.scan_count')} ↓`},
        { value: '{ "column": "scanCount", "direction": "asc", "clientSideSort": true, "type": "numeric" }', text: `${t('scan_points.scan_count')} ↑`},
    ];

    const fetchScanPoints = useCallback((params) => {
        stateDispatch({ type: 'LOADING', loading: true });

        api.scanpoint.getScanPoints(eventID, partnerID, {
            ...params,
            ...(params.clientSideSort ? { page: 1, limit: 100 } : {})
        }).then((response) => {
            if (response.data.data.scanPoints) {
                const { scanPoints } = response.data.data;
                const { meta } = response.data;
                stateDispatch({
                    type: 'SET_SCAN_POINTS',
                    scanPoints: params.clientSideSort ? handleClientSideSorting(scanPoints, params.sorter) : scanPoints,
                    scanPointsTotal: meta.rowsTotal,
                    pageCount: Math.ceil(meta.rowsTotal / meta.limit),
                });
            } else {
                stateDispatch({ type: 'LOADING', loading: false });
            }
        })
        .catch((error) => {
            stateDispatch({ type: 'LOADING', loading: false });
            dispatch(toastrAdd({
                code: 0,
                type: 'error',
                title: t('errors.scan_points_error'),
                details: `${t('general.details')}: ${error.firstError.title} - ${error.firstError.details}`
            }));
        });
    }, [eventID, partnerID, dispatch, t]);

    useEffect(() => {
        fetchScanPoints(params);
    }, [fetchScanPoints, params]);

    const handlePageSize = ({ target }) => {
        stateDispatch({ type: 'SET_PARAMS', params: {
            ...params,
            page: 1,
            limit: target.value
        }});
    };

    const handleSearch = ({ target }) => {
        stateDispatch({ type: 'SET_PARAMS', params: {
            ...params,
            page: 1,
            search: target.value.split(' ') || null
        }});
    };

    const handleSorting = ({ target }) => {
        const sorter = JSON.parse(target.value);
        
        stateDispatch({
            type: 'SET_PARAMS',
            params: {
                ...params,
                sorter,
                clientSideSort: sorter.clientSideSort || false
            }
        });
    };

    const paginate = ({ selected }) => {
        stateDispatch({ type: 'SET_PARAMS', params: {
            ...params,
            page: selected + 1
        }});
    };

    return (
        <>
            {!overView ?
                <div className="container DataListInputs">
                    <div className="row justify-content align-items-center">
                        <div className="col col-sm-auto">
                            <select
                                className="form-control form-control-sm"
                                defaultValue={25}
                                onChange={handlePageSize}
                                disabled={params.clientSideSort}
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                        <div className="col col-sm-auto">
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder={t('general.search')}
                                onChange={handleSearch}
                            />
                        </div>
                        <div className="col col-sm-auto">
                            <select
                                className="form-control form-control-sm"
                                defaultValue="empty"
                                onChange={handleSorting}
                            >
                                <option disabled value="empty"></option>
                                {sortingOptions.map((o, i) => (
                                    <option
                                        key={i}
                                        value={o.value}
                                    >
                                        {o.text}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            : null}

            <div className="DataList">
                {scanPoints.map(scanPoint => (
                    <ScanPointsListItem
                        key={scanPoint.scanPointID}
                        scanPoint={scanPoint}
                        eventID={eventID}
                    />
                ))}
                {loading ?
                    <LoadingOverlay />
                : null}
            </div>

            {scanPoints.length ?
                <Card.Footer>
                    <div className="pull-right">
                        <Pagination 
                            pageCount={pageCount}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            onPageChange={paginate}
                            forcePage={params.page - 1}
                        />
                    </div>
                </Card.Footer>
            : null}
            
            {!scanPoints.length ?
                <Card.Body>
                    {scanPointsTotal > 0 || !overView || !isScanPointSalesEnabled
                        ?
                            <>{t('scan_points.no_filtered_scan_points')}</>
                        : 
                            <>{t('scan_points.no_scan_points_helper1')} <NavLink to={`/events/${eventID}/scanpoints/order`}>{t('scan_points.no_scan_points_helper2')}</NavLink>.</>
                    }
                </Card.Body>
            : null}
        </>
    );
}

export default ScanPointsList;